import { FormattedText, SpanTag } from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import React, { useState } from "react";

import { Timeline } from "../../../../components/Timeline";
import type {
  TimelineItem,
  TimelineState,
} from "../../../../components/Timeline/types";
import {
  Card,
  CardCtaButtonList,
  CardCtaDivider,
  CardHeaderRow,
  CardPadding,
} from "../../OrderDetailV2.style";
import type {
  ApiOrderDetailsRefundTrackerData,
  ApiOrderDetailsRefundTrackerTimelineItem,
} from "../../types/api";
import { TextButton } from "../TextButton";
import {
  AmountBreakdownRows,
  AmountRow,
  AmountSummaryRow,
  AmountSummarySection,
  CopyButton,
  TimelineDetailRow,
  TimelineItemContent,
  TimelineItemDetailRows,
  TimelineItemHelperText,
  TimelineItemSubTitle,
  TimelineItemTitle,
  TimelineSection,
} from "./OrderDetailV2RefundTracker.style";
import { RefundCalculationModal } from "./RefundCalculationModal";

// ── Helpers ───────────────────────────────────────────────────────────────────

const toTimelineState = (state?: string): TimelineState => {
  if (
    state === "completed" ||
    state === "in_progress" ||
    state === "pending" ||
    state === "error"
  ) {
    return state;
  }
  return "pending";
};

const buildTimelineItems = (
  apiItems: ApiOrderDetailsRefundTrackerTimelineItem[],
  copyIconUrl?: string,
  onCopy?: (text: string) => void
): TimelineItem[] =>
  apiItems.map((item, index) => ({
    id: `refund-tracker-item-${index}`,
    state: toTimelineState(item.state),
    content: (
      <TimelineItemContent>
        {item.title && (
          <TimelineItemTitle>
            <SpanTag
              fontWeight="black"
              fontSize="sm"
              style={{ color: theme.greyText }}
            >
              <FormattedText value={item.title} />
            </SpanTag>
          </TimelineItemTitle>
        )}
        {item.subTitle && (
          <TimelineItemSubTitle>
            <SpanTag fontSize="xs" style={{ color: theme.greyText6 }}>
              <FormattedText value={item.subTitle} />
            </SpanTag>
          </TimelineItemSubTitle>
        )}
        {item.detailRows && item.detailRows.length > 0 && (
          <TimelineItemDetailRows>
            {item.detailRows.map((row, rowIndex) => (
              <TimelineDetailRow key={rowIndex}>
                {row.label && (
                  <SpanTag fontSize="xs" style={{ color: theme.greyText6 }}>
                    <FormattedText value={row.label} />
                  </SpanTag>
                )}
                {row.value && (
                  <SpanTag
                    fontSize="xs"
                    fontWeight="bold"
                    style={{ color: theme.blueText }}
                  >
                    <FormattedText value={row.value} />
                  </SpanTag>
                )}
                {row.copyEnabled && copyIconUrl && onCopy && row.value && (
                  <CopyButton
                    aria-label="Copy"
                    onClick={() => onCopy(String(row.value))}
                  >
                    <img src={copyIconUrl} alt="copy" width={14} height={14} />
                  </CopyButton>
                )}
              </TimelineDetailRow>
            ))}
          </TimelineItemDetailRows>
        )}
        {item.helperText && (
          <TimelineItemHelperText>
            <SpanTag fontSize="xs" style={{ color: theme.greyText6 }}>
              <FormattedText value={item.helperText} />
            </SpanTag>
          </TimelineItemHelperText>
        )}
      </TimelineItemContent>
    ),
  }));

// ── Component ─────────────────────────────────────────────────────────────────

export const OrderDetailV2RefundTracker: React.FC<
  ApiOrderDetailsRefundTrackerData
> = ({
  header,
  amountSummary,
  timeline,
  actionsList,
  refundCalculationBottomSheet,
}) => {
  const device = useBreakpointV2();
  const isMobile = device === "sm";
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
  };

  const timelineItems = buildTimelineItems(
    timeline?.items ?? [],
    timeline?.copyIconUrl,
    handleCopy
  );

  const visibleActions = (actionsList ?? []).filter(
    (action) => action.isVisible !== false
  );
  const primaryAction = visibleActions[0];

  const hasTimeline = timelineItems.length > 0;
  const hasAmountBreakdown =
    Array.isArray(amountSummary?.rows) &&
    (amountSummary?.rows?.length ?? 0) > 0;

  return (
    <Card>
      <CardPadding>
        <CardHeaderRow>
          {header?.title && (
            <SpanTag fontWeight="black" fontSize="large">
              <FormattedText value={header.title} />
            </SpanTag>
          )}
          {!isMobile && primaryAction && (
            <TextButton
              btnTxt={primaryAction.label ?? primaryAction.text ?? ""}
              onClick={() => setIsModalOpen(true)}
              disabled={primaryAction.isEnabled === false}
              showDefaultRightIcon={false}
            />
          )}
        </CardHeaderRow>

        {amountSummary && (
          <AmountSummarySection>
            <AmountSummaryRow>
              {amountSummary.title && (
                <SpanTag fontWeight="bold" fontSize="md">
                  <FormattedText value={amountSummary.title} />
                </SpanTag>
              )}
              {amountSummary.value && (
                <SpanTag
                  fontWeight="black"
                  fontSize="large"
                  style={{ textAlign: "right", flexShrink: 0 }}
                >
                  <FormattedText value={amountSummary.value} />
                </SpanTag>
              )}
            </AmountSummaryRow>
            {hasAmountBreakdown && (
              <AmountBreakdownRows>
                {(amountSummary.rows ?? []).map((row, index) => (
                  <AmountRow key={index}>
                    <SpanTag
                      fontSize="sm"
                      style={{ color: theme.greyText, flex: 1 }}
                    >
                      <FormattedText value={row.label} />
                    </SpanTag>
                    <SpanTag
                      fontSize="sm"
                      style={{
                        color: theme.greyText,
                        textAlign: "right",
                        flexShrink: 0,
                      }}
                    >
                      <FormattedText value={row.value} />
                    </SpanTag>
                  </AmountRow>
                ))}
              </AmountBreakdownRows>
            )}
          </AmountSummarySection>
        )}

        {hasTimeline && (
          <TimelineSection>
            <Timeline
              items={timelineItems}
              direction={isMobile ? "vertical" : "horizontal"}
              markerSize={20}
              connectorWidth={2}
              itemGap={isMobile ? 16 : 0}
              contentGap={20}
            />
          </TimelineSection>
        )}
      </CardPadding>

      {isMobile && primaryAction && (
        <>
          <CardCtaDivider />
          <CardCtaButtonList>
            <TextButton
              fullWidth
              btnTxt={primaryAction.label ?? primaryAction.text ?? ""}
              onClick={() => setIsModalOpen(true)}
              disabled={primaryAction.isEnabled === false}
            />
          </CardCtaButtonList>
        </>
      )}

      {refundCalculationBottomSheet && (
        <RefundCalculationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={refundCalculationBottomSheet.title}
          lineItems={refundCalculationBottomSheet.lineItems}
          refundSummary={refundCalculationBottomSheet.refundSummary}
          closeLabel={refundCalculationBottomSheet.closeLabel}
        />
      )}
    </Card>
  );
};

export default OrderDetailV2RefundTracker;
